import {
  formatOrgNumber,
  providerCompanySchema,
  providerDetailsSchema,
  type ProviderCompanyForm,
  type ProviderProfile,
} from '@hemmafixa/shared';
import {
  Button,
  Card,
  Checkbox,
  DescriptionList,
  FormField,
  Input,
  Label,
  PageHeader,
  StepForm,
  Textarea,
  toast,
} from '@hemmafixa/ui';
import { BadgeCheck, Clock, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { fill, useI18n } from '@/i18n/context';
import type { Messages } from '@/i18n/sv';
import { ApiError } from '@/lib/api';
import { useCategoryLabel, useCounties } from '../shared/catalogue';
import { QueryView } from '../shared/query-view';
import { useProviderProfile, useSaveCompany, useSaveDetails, useSaveServices, useSubmitProfile } from './api';
import { ProviderDocuments } from './provider-documents';
import { ServicesPicker } from './services-picker';

type CompanyErrors = Partial<Record<keyof ProviderCompanyForm, string>>;

/**
 * The provider's company profile: a wizard while onboarding or rejected (P2-09), a status card while pending, and
 * once verified the parts the provider may still change themselves (P2-11).
 */
export function OnboardingPage() {
  const { t } = useI18n();
  const query = useProviderProfile();
  const s = t.provider.onboarding;

  return (
    <div className="grid gap-4">
      <PageHeader
        title={s.title}
        description={query.data?.status === 'verified' ? s.descriptionVerified : s.description}
      />
      <QueryView query={query} loading="detail">
        {(profile) =>
          !profile ? null : profile.status === 'verified' ? (
            <VerifiedProfile profile={profile} />
          ) : profile.status === 'pending' ? (
            <StatusCard profile={profile} />
          ) : (
            <Wizard profile={profile} />
          )
        }
      </QueryView>
    </div>
  );
}

function StatusCard({ profile }: { profile: ProviderProfile }) {
  const { t } = useI18n();
  const s = t.provider.onboarding;
  return (
    <>
      <Notice
        icon={<Clock className="size-5 text-warning" aria-hidden />}
        title={s.pendingTitle}
        text={s.pendingText}
      />
      <Card className="p-5 sm:p-6">
        <Summary profile={profile} />
      </Card>
    </>
  );
}

/** Verified: the checked company details are read-only; description, website and services can change. */
function VerifiedProfile({ profile }: { profile: ProviderProfile }) {
  const { t } = useI18n();
  const s = t.provider.onboarding;
  return (
    <>
      <Notice
        icon={<BadgeCheck className="size-5 text-success" aria-hidden />}
        title={s.verifiedTitle}
        text={s.verifiedText}
      >
        <Link
          to="/provider/onboarding/public"
          className="mt-1 justify-self-start text-sm font-medium underline underline-offset-2"
        >
          {s.viewPublic}
        </Link>
      </Notice>
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="grid min-w-0 gap-4">
          <ServicesForm profile={profile} />
          <DetailsForm profile={profile} />
        </div>
        <div className="grid gap-4 xl:sticky xl:top-18">
          <Card className="grid gap-3 p-5">
            <h2 className="font-semibold">{s.checkedDetails}</h2>
            <p className="text-sm text-muted-foreground">{s.verifiedEditText}</p>
            <DescriptionList
              items={[
                { label: s.companyName, value: profile.companyName ?? s.notSet },
                {
                  label: s.orgNumber,
                  value: profile.orgNumber ? formatOrgNumber(profile.orgNumber) : s.notSet,
                },
                { label: s.fSkatt, value: profile.hasFSkatt ? t.common.yes : t.common.no },
              ]}
            />
          </Card>
          <Card className="grid gap-3 p-5">
            <h2 className="font-semibold">{s.documentsTitle}</h2>
            <p className="text-sm text-muted-foreground">{s.documentsVerifiedText}</p>
            <ProviderDocuments editable />
          </Card>
        </div>
      </div>
    </>
  );
}

function DetailsForm({ profile }: { profile: ProviderProfile }) {
  const { t } = useI18n();
  const s = t.provider.onboarding;
  const save = useSaveDetails();
  const [about, setAbout] = useState(profile.about ?? '');
  const [website, setWebsite] = useState(profile.website ?? '');
  const [websiteError, setWebsiteError] = useState<string>();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = providerDetailsSchema.safeParse({ about, website });
    if (!parsed.success) {
      setWebsiteError(s.websiteInvalid);
      return;
    }
    setWebsiteError(undefined);
    save.mutate(
      { about, website },
      {
        onSuccess: (saved) => {
          setWebsite(saved.website ?? '');
          toast.success(s.savedToast);
        },
        onError: (error) => {
          if (error instanceof ApiError && error.fields?.some((field) => field.path === 'website')) {
            setWebsiteError(s.websiteInvalid);
            return;
          }
          toast.error(error instanceof ApiError && error.status === 0 ? t.common.errorMessage : s.saveFailed);
        },
      },
    );
  };

  return (
    <Card className="p-5 sm:p-6">
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <h2 className="font-semibold">{s.publicDetails}</h2>
        <FormField label={s.about} hint={s.aboutHint}>
          <Textarea
            rows={5}
            maxLength={1000}
            value={about}
            onChange={(event) => setAbout(event.target.value)}
          />
        </FormField>
        <FormField label={s.website} error={websiteError}>
          <Input
            type="url"
            inputMode="url"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
            placeholder="www.exempel.se"
          />
        </FormField>
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>
            {s.saveDetails}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ServicesForm({ profile }: { profile: ProviderProfile }) {
  const { t } = useI18n();
  const s = t.provider.onboarding;
  const save = useSaveServices();
  const [categories, setCategories] = useState(profile.categorySlugs);
  const [counties, setCounties] = useState(profile.countyCodes);
  const [errors, setErrors] = useState<{ categories?: string; counties?: string }>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = {
      categories: categories.length === 0 ? s.chooseCategory : undefined,
      counties: counties.length === 0 ? s.chooseCounty : undefined,
    };
    setErrors(found);
    if (found.categories || found.counties) return;
    save.mutate(
      { categorySlugs: categories, countyCodes: counties },
      {
        onSuccess: () => toast.success(s.savedToast),
        onError: (error) =>
          toast.error(error instanceof ApiError && error.status === 0 ? t.common.errorMessage : s.saveFailed),
      },
    );
  };

  return (
    <Card className="p-5 sm:p-6">
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <div className="grid gap-1">
          <h2 className="font-semibold">{s.steps.services}</h2>
          <p className="text-sm text-muted-foreground">{s.servicesHint}</p>
        </div>
        <ServicesPicker
          categories={categories}
          counties={counties}
          onCategories={setCategories}
          onCounties={setCounties}
          errors={errors}
        />
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>
            {s.saveServices}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Notice({
  icon,
  title,
  text,
  children,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  children?: ReactNode;
}) {
  return (
    <Card className="flex gap-3 p-5" role="status">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="grid gap-1">
        <h2 className="font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{text}</p>
        {children}
      </div>
    </Card>
  );
}

function Wizard({ profile }: { profile: ProviderProfile }) {
  const { t } = useI18n();
  const s = t.provider.onboarding;
  const steps = [s.steps.company, s.steps.services, s.steps.documents, s.steps.review];
  const [step, setStep] = useState(() => (!profile.steps.company ? 0 : !profile.steps.services ? 1 : 3));
  const saveCompany = useSaveCompany();
  const saveServices = useSaveServices();
  const submit = useSubmitProfile();

  const [company, setCompany] = useState<ProviderCompanyForm>({
    companyName: profile.companyName ?? '',
    orgNumber: profile.orgNumber ? formatOrgNumber(profile.orgNumber) : '',
    hasFSkatt: profile.hasFSkatt as true,
    about: profile.about ?? '',
    website: profile.website ?? '',
  });
  const [companyErrors, setCompanyErrors] = useState<CompanyErrors>({});
  const [categories, setCategories] = useState<string[]>(profile.categorySlugs);
  const [counties, setCounties] = useState<string[]>(profile.countyCodes);
  const [servicesError, setServicesError] = useState<{ categories?: string; counties?: string }>({});

  const failed = (error: Error) =>
    toast.error(error instanceof ApiError && error.status === 0 ? t.common.errorMessage : s.saveFailed);

  const next = () => {
    if (step === 0) {
      const parsed = providerCompanySchema.safeParse(company);
      if (!parsed.success) {
        const fields = parsed.error.flatten().fieldErrors;
        setCompanyErrors(Object.fromEntries(Object.keys(fields).map((key) => [key, companyMessage(key, s)])));
        return;
      }
      setCompanyErrors({});
      saveCompany.mutate(company, {
        onSuccess: () => setStep(1),
        onError: (error) => {
          if (error instanceof ApiError && error.fields?.some((field) => field.path === 'orgNumber')) {
            setCompanyErrors({ orgNumber: s.orgNumberTaken });
          } else failed(error);
        },
      });
    } else if (step === 1) {
      const errors = {
        categories: categories.length === 0 ? s.chooseCategory : undefined,
        counties: counties.length === 0 ? s.chooseCounty : undefined,
      };
      setServicesError(errors);
      if (errors.categories || errors.counties) return;
      saveServices.mutate(
        { categorySlugs: categories, countyCodes: counties },
        { onSuccess: () => setStep(2), onError: failed },
      );
    } else if (step === 2) {
      setStep(3);
    } else {
      submit.mutate(undefined, { onSuccess: () => toast.success(s.doneToast), onError: failed });
    }
  };

  return (
    <>
      {profile.status === 'rejected' && (
        <Notice
          icon={<TriangleAlert className="size-5 text-danger" aria-hidden />}
          title={s.rejectedTitle}
          text={s.rejectedText}
        >
          {profile.rejectionReason && (
            <p className="whitespace-pre-line text-sm">{profile.rejectionReason}</p>
          )}
        </Notice>
      )}
      <StepForm
        steps={steps}
        current={step}
        title={steps[step]!}
        description={s.stepText[step]}
        onBack={() => setStep((current) => current - 1)}
        onNext={next}
        loading={saveCompany.isPending || saveServices.isPending || submit.isPending}
        labels={{
          back: t.common.back,
          next: t.common.next,
          finish: s.submit,
          progress: (current, total) => fill(s.progress, { current, total }),
        }}
      >
        {step === 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={s.companyName} error={companyErrors.companyName} required>
              <Input
                autoComplete="organization"
                maxLength={120}
                value={company.companyName}
                onChange={(event) => setCompany({ ...company, companyName: event.target.value })}
              />
            </FormField>
            <FormField label={s.orgNumber} hint={s.orgNumberHint} error={companyErrors.orgNumber} required>
              <Input
                inputMode="numeric"
                maxLength={13}
                value={company.orgNumber}
                onChange={(event) => setCompany({ ...company, orgNumber: event.target.value })}
              />
            </FormField>
            <FormField
              label={s.about}
              hint={s.aboutHint}
              error={companyErrors.about}
              className="sm:col-span-2"
            >
              <Textarea
                rows={3}
                maxLength={1000}
                value={company.about ?? ''}
                onChange={(event) => setCompany({ ...company, about: event.target.value })}
              />
            </FormField>
            <FormField label={s.website} error={companyErrors.website} className="sm:col-span-2">
              <Input
                type="url"
                inputMode="url"
                placeholder="www.example.se"
                maxLength={200}
                value={company.website ?? ''}
                onChange={(event) => setCompany({ ...company, website: event.target.value })}
              />
            </FormField>
            <div className="grid gap-1 sm:col-span-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="fskatt"
                  checked={company.hasFSkatt}
                  aria-describedby="fskatt-hint"
                  onCheckedChange={(checked) =>
                    setCompany({ ...company, hasFSkatt: (checked === true) as true })
                  }
                />
                <Label htmlFor="fskatt">{s.fSkatt}</Label>
              </div>
              <p
                id="fskatt-hint"
                className={companyErrors.hasFSkatt ? 'text-sm text-danger' : 'text-sm text-muted-foreground'}
              >
                {companyErrors.hasFSkatt ?? s.fSkattHint}
              </p>
            </div>
          </div>
        )}
        {step === 1 && (
          <ServicesPicker
            categories={categories}
            counties={counties}
            onCategories={setCategories}
            onCounties={setCounties}
            errors={servicesError}
          />
        )}
        {step === 2 && (
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">{s.documentsText}</p>
            <ProviderDocuments editable />
          </div>
        )}
        {step === 3 && <Summary profile={profile} onEdit={setStep} />}
      </StepForm>
    </>
  );
}

/** The schema's own texts are English; the form shows translated ones. */
function companyMessage(key: string, s: Messages['provider']['onboarding']) {
  if (key === 'hasFSkatt') return s.fSkattHint;
  if (key === 'orgNumber') return s.orgNumberInvalid;
  return s.invalidField;
}

/** The saved profile; with onEdit, each part links back to its step. */
function Summary({ profile, onEdit }: { profile: ProviderProfile; onEdit?: (step: number) => void }) {
  const { t } = useI18n();
  const s = t.provider.onboarding;
  const categoryLabel = useCategoryLabel();
  const counties = useCounties();
  const countyName = (code: string) => counties.data?.find((county) => county.code === code)?.name ?? code;
  const editButton = (step: number) =>
    onEdit && (
      <Button variant="ghost" size="sm" onClick={() => onEdit(step)}>
        {s.edit}
      </Button>
    );

  return (
    <div className="grid gap-4">
      <section className="grid gap-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold">{s.steps.company}</h3>
          {editButton(0)}
        </div>
        <DescriptionList
          items={[
            { label: s.companyName, value: profile.companyName ?? s.notSet },
            { label: s.orgNumber, value: profile.orgNumber ? formatOrgNumber(profile.orgNumber) : s.notSet },
            { label: s.fSkatt, value: profile.hasFSkatt ? t.common.yes : t.common.no },
            ...(profile.website ? [{ label: s.website, value: profile.website }] : []),
            ...(profile.about ? [{ label: s.about, value: profile.about }] : []),
          ]}
        />
      </section>
      <section className="grid gap-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold">{s.steps.services}</h3>
          {editButton(1)}
        </div>
        <DescriptionList
          items={[
            { label: s.categories, value: profile.categorySlugs.map(categoryLabel).join(', ') || s.notSet },
            { label: s.counties, value: profile.countyCodes.map(countyName).join(', ') || s.notSet },
          ]}
        />
      </section>
    </div>
  );
}
